import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import { useQueries } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Stack } from 'src/components/Stack';
import { TitleAndClose } from 'src/components/TitleAndClose';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { externalLinks } from 'src/externalLinks';
import {
  fetchInvoices,
  isPayableOpenInvoice,
  pastDueInvoicesQueryKey,
} from 'src/models/payment/payment';
import { isTrainer } from 'src/models/user/utils';
import { PayBalanceContent } from 'src/shared/PayBalance/PayBalanceContent';

import { ActionsList } from './ActionsList';
import { CheckinPastDuePaymentDialog } from './CheckinPastDuePaymentDialog';
import { SessionContent } from './SessionContent';
import { UserContent } from './UserContent';
import {
  BlockingReasonAction,
  BlockingReasonButtonAction,
  CheckinBlocker,
  CheckinBlockerContentProps,
  CheckinBlockerUser,
} from './_types';
import { blockingReasonTertiaryButtonSx } from './blockingReasonStyles';

export type { CheckinBlockerContentProps } from './_types';

function hasUnresolvedBillingBlocker(user: CheckinBlockerUser) {
  return user.blockers.some(
    (blocker) => blocker.type === 'billing_blocked' && !blocker.resolved
  );
}

/**
 * Shared blocking-reason body used by both the CheckinBlockerDialog (React)
 * and future fullscreen/iframe pages (embedded in Ember).
 *
 * This component is intentionally presentation-only — keep host-specific
 * chrome (dialog padding, page layout, scroll wrappers) in the consumer.
 */
export function CheckinBlockerContent({
  users,
  showUserDetails = false,
  blockingReasonVariant = 'checkin',
  onOverride,
  onBillingResolved,
  sessionDetails,
  fillAvailableSpace = false,
}: CheckinBlockerContentProps) {
  const { t } = useTranslation('translation', { keyPrefix: 'checkinBlocker' });
  const { t: tPayBalance } = useTranslation('translation', {
    keyPrefix: 'payBalance',
  });
  const { user: sessionUser } = useOptionalSession();
  const [paymentDialogUserId, setPaymentDialogUserId] = useState<string>();
  const isStaffFlow = (!!sessionUser && isTrainer(sessionUser)) || !!onOverride;
  const canLoadPastDueInvoices = !!sessionUser || !!onOverride;
  const safeUsers = canLoadPastDueInvoices
    ? users
    : users.map((user) => ({
        ...user,
        blockers: user.blockers.map((blocker) => {
          if (!['billing_blocked', 'paymentRequired'].includes(blocker.type)) {
            return blocker;
          }

          return {
            ...blocker,
            type: 'custom' as const,
          };
        }),
      }));

  const billingBlockedUserIds = [
    ...new Set(
      users
        .filter(
          (user) => canLoadPastDueInvoices && hasUnresolvedBillingBlocker(user)
        )
        .map((u) => String(u.id))
    ),
  ];

  const pastDueInvoiceQueries = useQueries({
    queries: billingBlockedUserIds.map((userId) => ({
      queryKey: pastDueInvoicesQueryKey(userId),
      queryFn: () =>
        fetchInvoices({
          userId,
          per: 100,
          filters: { pastDue: true },
        }),
      enabled: billingBlockedUserIds.length > 0,
    })),
  });

  const userIdHasPayablePastDue = (userId: string) => {
    const id = String(userId);
    const idx = billingBlockedUserIds.indexOf(id);
    if (idx < 0) return false;
    const q = pastDueInvoiceQueries[idx];
    if (!q?.data || q.isError) return false;
    return q.data.invoices.some(isPayableOpenInvoice);
  };

  const getPastDueInvoiceQuery = (userId: string) => {
    const idx = billingBlockedUserIds.indexOf(String(userId));
    if (idx < 0) return undefined;
    return pastDueInvoiceQueries[idx];
  };

  const isPastDueInvoiceInfoLoading = (userId: string) => {
    const q = getPastDueInvoiceQuery(userId);
    if (!q || q.isError) return false;
    return q.isPending || (q.isFetching && q.data === undefined);
  };

  const shouldShowPayBalanceAction = (userId: string) => {
    const q = getPastDueInvoiceQuery(userId);
    if (!q || q.isError) return false;
    if (isPastDueInvoiceInfoLoading(userId)) return true;
    return userIdHasPayablePastDue(userId);
  };

  const hasMultipleUsers = users.length > 1;
  const firstUser = users[0];
  const unresolvedBlockerCount = users.reduce(
    (count, user) =>
      count + user.blockers.filter((blocker) => !blocker.resolved).length,
    0
  );
  const hasMultipleBlockers = unresolvedBlockerCount > 1;

  const getGlobalOverrideLabel = () =>
    hasMultipleUsers || hasMultipleBlockers
      ? t('actions.overrideAndCheckInAll')
      : t('actions.overrideAndCheckIn');

  const buildTertiaryButtonAction = (
    label: string,
    onClick: () => void,
    options: Partial<BlockingReasonButtonAction> = {}
  ): BlockingReasonAction => ({
    label,
    variant: 'tertiary',
    color: 'primary',
    onClick,
    sx: blockingReasonTertiaryButtonSx,
    ...options,
  });

  const buildPayBalanceAction = (
    user: CheckinBlockerUser,
    loading: boolean
  ): BlockingReasonAction =>
    buildTertiaryButtonAction(
      t('actions.updatePaymentMethod'),
      () => setPaymentDialogUserId(user.id),
      { loading, disabled: loading }
    );

  const buildViewDocumentsAction = (
    user: CheckinBlockerUser,
    blocker: CheckinBlocker
  ): BlockingReasonAction => {
    const count = blocker.unsignedDocumentCount ?? 1;
    const label =
      count > 1 ? t('actions.viewDocuments') : t('actions.viewDocument');

    return buildTertiaryButtonAction(label, () => {
      if (count === 1 && blocker.documentUrl) {
        window.open(blocker.documentUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      window.open(
        externalLinks.absolute(`/ex4/clients/${user.id}/signatures`),
        '_blank',
        'noopener,noreferrer'
      );
    });
  };

  const buildGlobalOverrideAction = (
    onClick: () => void
  ): BlockingReasonAction =>
    buildTertiaryButtonAction(getGlobalOverrideLabel(), onClick);

  const buildOverrideAction = (
    user: CheckinBlockerUser,
    blocker: CheckinBlockerUser['blockers'][number],
    blockerIndex: number
  ): BlockingReasonAction => ({
    kind: 'link',
    label: t('actions.override'),
    onClick: () =>
      void onOverride?.({
        scope: 'blocker',
        user,
        blocker,
        blockerIndex,
      }),
  });

  const globalActions: BlockingReasonAction[] | undefined = (() => {
    if (!firstUser || !onOverride) return undefined;

    if (hasMultipleUsers) {
      return [
        buildGlobalOverrideAction(() => void onOverride({ scope: 'all' })),
      ];
    }

    if (hasMultipleBlockers) {
      return [
        buildGlobalOverrideAction(
          () => void onOverride({ scope: 'user', user: firstUser })
        ),
      ];
    }

    return undefined;
  })();

  const getBlockerActions = (
    user: CheckinBlockerUser,
    blocker: CheckinBlockerUser['blockers'][number],
    blockerIndex: number
  ): BlockingReasonAction[] | undefined => {
    if (blocker.resolved) return undefined;

    const actions: BlockingReasonAction[] = [];
    const userUnresolvedBlockerCount = user.blockers.filter(
      (item) => !item.resolved
    ).length;
    const isSingleUnresolvedBlockerOnUser = userUnresolvedBlockerCount === 1;
    const showPerBlockerOverride =
      !!onOverride && (hasMultipleUsers || user.blockers.length > 1);

    if (blocker.type === 'waiver_required' && isStaffFlow) {
      actions.push(buildViewDocumentsAction(user, blocker));
    }

    if (
      blocker.type === 'billing_blocked' &&
      shouldShowPayBalanceAction(String(user.id))
    ) {
      actions.push(
        buildPayBalanceAction(
          user,
          isPastDueInvoiceInfoLoading(String(user.id))
        )
      );
    }

    if (showPerBlockerOverride) {
      actions.push(buildOverrideAction(user, blocker, blockerIndex));
    } else if (
      onOverride &&
      !hasMultipleUsers &&
      isSingleUnresolvedBlockerOnUser
    ) {
      actions.push({
        kind: 'link',
        label: t('actions.override'),
        onClick: () => void onOverride({ scope: 'user', user }),
      });
    }

    return actions.length > 0 ? actions : undefined;
  };

  const closePayBalance = () => setPaymentDialogUserId(undefined);

  const shellSx = {
    bgcolor: 'background.paper',
    width: '100%',
    ...(fillAvailableSpace && {
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      minHeight: '100%',
    }),
  } as const;

  // Member self-pay: swap blocker body for Pay Balance (no nested Dialog / second X).
  // Staff / trainer override hosts keep CheckinPastDuePaymentDialog.
  if (paymentDialogUserId && !isStaffFlow) {
    return (
      <Box sx={shellSx}>
        <TitleAndClose
          title={tPayBalance('title')}
          leftAdornment={
            <IconButton
              aria-label="back"
              color="secondary"
              onClick={closePayBalance}
            >
              <Icon icon={faArrowLeft} />
            </IconButton>
          }
        />
        <Box
          sx={{
            px: 3,
            pb: 2,
            ...(fillAvailableSpace && {
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              minHeight: 0,
            }),
          }}
        >
          <PayBalanceContent
            userId={paymentDialogUserId}
            fixedFooter={false}
            onPaymentSuccess={() => {
              onBillingResolved?.(paymentDialogUserId);
              closePayBalance();
            }}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={shellSx}>
      <Stack
        alignItems="stretch"
        spacing={3}
        sx={{
          pb: 2,
          pt: fillAvailableSpace ? 2 : 1,
          px: 3,
          ...(fillAvailableSpace && {
            flex: 1,
            minHeight: 0,
          }),
        }}
      >
        {sessionDetails ? (
          <SessionContent
            users={safeUsers}
            sessionDetails={sessionDetails}
            actions={globalActions}
            getBlockerActions={getBlockerActions}
            fillAvailableSpace={fillAvailableSpace}
          />
        ) : (
          <>
            <UserContent
              users={safeUsers}
              getBlockerActions={getBlockerActions}
              showUserDetails={showUserDetails}
              variant={blockingReasonVariant}
            />
            {!!globalActions && (
              <Box sx={{ pt: 1 }}>
                <ActionsList actions={globalActions} />
              </Box>
            )}
          </>
        )}
      </Stack>
      {isStaffFlow && (
        <CheckinPastDuePaymentDialog
          open={!!paymentDialogUserId}
          onClose={closePayBalance}
          onPaymentResolved={() => {
            if (paymentDialogUserId) {
              onBillingResolved?.(paymentDialogUserId);
            }
          }}
          userId={paymentDialogUserId ?? ''}
        />
      )}
    </Box>
  );
}
